import {
 Link
} from 'react-router-dom'

export function ZoomLandingPage(): JSX.Element {
  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="bg-white rounded-2xl shadow-soft-lg p-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 bg-[#2D8CFF] rounded-2xl flex items-center justify-center">
            <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M4.585 4.585C2.766 4.585 1.293 6.058 1.293 7.877v8.246c0 1.819 1.473 3.292 3.292 3.292h10.169a.727.727 0 00.727-.727v-3.292l3.973 2.98a.727.727 0 001.162-.582V6.206a.727.727 0 00-1.162-.582l-3.973 2.98V5.312a.727.727 0 00-.727-.727H4.585zm0 1.454h9.896v6.019a.727.727 0 001.162.582l3.973-2.98v5.68l-3.973-2.98a.727.727 0 00-1.162.582v2.727H4.585a1.836 1.836 0 01-1.838-1.838V7.877c0-1.014.824-1.838 1.838-1.838z"/>
            </svg>
          </div>
          <div>
            <h1 className="text-4xl font-bold text-gray-900">Catch for Zoom</h1>
            <p className="text-gray-600 text-lg">Create and manage Zoom meetings seamlessly</p>
          </div>
        </div>

        <div className="prose prose-lg max-w-none">
          {/* Overview Section */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">What does the Zoom integration do?</h2>
            <p className="text-gray-700 mb-4">
              The Zoom integration allows Catch to create Zoom meetings directly from your calendar,
              manage meeting participants, and track meeting durations. When you schedule a meeting
              that needs a video call, Catch automatically generates a Zoom link and adds it to the
              calendar invite.
            </p>
            <p className="text-gray-700">
              This integration helps streamline your scheduling workflow by eliminating the need to
              manually create Zoom meetings and copy-paste links into calendar events.
            </p>
          </section>

          {/* How It Works Section */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-gray-700 mb-4">
              Once connected, Catch can use your Zoom account to enhance your scheduling experience:
            </p>

            <div className="bg-gray-50 rounded-xl p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Example Conversations</h3>
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <p className="text-sm text-gray-500 mb-1">You:</p>
                  <p className="text-gray-800">"Schedule a Zoom call with John tomorrow at 3pm"</p>
                  <p className="text-sm text-gray-500 mt-3 mb-1">Catch:</p>
                  <p className="text-gray-800">"I've created a meeting with John for tomorrow at 3:00 PM and added a Zoom link. The invite has been sent."</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <p className="text-sm text-gray-500 mb-1">You:</p>
                  <p className="text-gray-800">"Add a Zoom link to my 2pm meeting"</p>
                  <p className="text-sm text-gray-500 mt-3 mb-1">Catch:</p>
                  <p className="text-gray-800">"Done! I've added a Zoom meeting link to your 2pm Product Review meeting."</p>
                </div>
              </div>
            </div>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">Features & Prerequisites</h3>
            <div className="space-y-4 mb-4">
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <h4 className="font-semibold text-gray-900">Create Zoom Meetings</h4>
                <p className="text-gray-700 text-sm mt-1">Automatically generate Zoom meetings with join links when scheduling events.</p>
                <p className="text-gray-500 text-sm mt-2"><strong>Prerequisite:</strong> Zoom account connected in Settings</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <h4 className="font-semibold text-gray-900">Add Zoom to Existing Events</h4>
                <p className="text-gray-700 text-sm mt-1">Attach Zoom links to calendar events that don't have one yet.</p>
                <p className="text-gray-500 text-sm mt-2"><strong>Prerequisite:</strong> Zoom account connected; existing calendar event</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <h4 className="font-semibold text-gray-900">Update Meeting Times</h4>
                <p className="text-gray-700 text-sm mt-1">When you reschedule a calendar event, the Zoom meeting time updates automatically.</p>
                <p className="text-gray-500 text-sm mt-2"><strong>Prerequisite:</strong> Existing meeting with Zoom link</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <h4 className="font-semibold text-gray-900">Cancel Meetings</h4>
                <p className="text-gray-700 text-sm mt-1">When you cancel a calendar event, the associated Zoom meeting is also deleted.</p>
                <p className="text-gray-500 text-sm mt-2"><strong>Prerequisite:</strong> Existing meeting with Zoom link</p>
              </div>
            </div>
          </section>

          {/* Installation Instructions */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">How to Connect</h2>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">Prerequisites</h3>
            <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
              <li>A Google account (required for Catch authentication)</li>
              <li>A Zoom account (free or paid)</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">Step-by-Step Instructions</h3>
            <ol className="list-decimal list-inside text-gray-700 mb-6 space-y-3">
              <li>
                <strong>Sign in to Catch</strong> - Visit our{' '}
                <Link to="/login" className="text-primary-600 hover:text-primary-700 underline">
                  login page
                </Link>{' '}
                and sign in with your Google account
              </li>
              <li>
                <strong>Go to Settings</strong> - Navigate to the{' '}
                <Link to="/integrations" className="text-primary-600 hover:text-primary-700 underline">
                  Settings page
                </Link>
              </li>
              <li>
                <strong>Click "Connect Zoom"</strong> - Find the Zoom integration section and click connect
              </li>
              <li>
                <strong>Authorize in Zoom</strong> - A popup will open with Zoom's authorization page. Review the permissions and click "Authorize"
              </li>
              <li>
                <strong>Confirmation</strong> - The popup closes and your Settings page shows "Connected"
              </li>
            </ol>

            <p className="text-gray-700 mb-4">
              Having trouble connecting?{' '}
              <a href="#troubleshooting" className="text-primary-600 hover:text-primary-700 underline">
                See troubleshooting tips below
              </a>.
            </p>
          </section>

          {/* Permissions Section */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Permissions</h2>
            <p className="text-gray-700 mb-4">
              When you connect your Zoom account, Catch requests the following permissions:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
              <li><strong>Read User Profile</strong> - Access your basic Zoom profile information</li>
              <li><strong>Read Meetings</strong> - View your scheduled Zoom meetings</li>
              <li><strong>Create Meetings</strong> - Create new Zoom meetings on your behalf</li>
              <li><strong>Update Meetings</strong> - Modify meeting details like participants</li>
              <li><strong>Read Past Meetings</strong> - Access completed meeting duration data</li>
            </ul>
          </section>

          {/* Data & Privacy Section */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Data & Privacy</h2>
            <p className="text-gray-700 mb-4">
              We take your privacy seriously. Here's how Catch handles your Zoom data:
            </p>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">What We Store</h3>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
              <li><strong>OAuth tokens</strong> - Encrypted using AWS KMS</li>
              <li><strong>Meeting IDs and join URLs</strong> - Encrypted at rest</li>
              <li><strong>Your Zoom User ID</strong> - For account association</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">What We Do NOT Access or Store</h3>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
              <li>Meeting recordings or content</li>
              <li>Meeting chat messages or transcripts</li>
              <li>Audio or video from your meetings</li>
              <li>Participant personal information</li>
              <li>Your Zoom billing information</li>
            </ul>

            <p className="text-gray-700 mb-4">
              To request access to, transfer, or deletion of your data, please contact us at{' '}
              <a href="mailto:privacy@catchagent.ai" className="text-primary-600 hover:text-primary-700 underline">
                privacy@catchagent.ai
              </a>
            </p>
            <p className="text-gray-700">
              For complete details, please read our{' '}
              <Link to="/privacy" className="text-primary-600 hover:text-primary-700 underline">
                Privacy Policy
              </Link>.
            </p>
          </section>

          {/* Disconnecting Section */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Removing the App</h2>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">Disconnect from Catch</h3>
            <ol className="list-decimal list-inside text-gray-700 mb-4 space-y-2">
              <li>Go to the <Link to="/integrations" className="text-primary-600 hover:text-primary-700 underline">Settings page</Link></li>
              <li>Find the Zoom integration section</li>
              <li>Click "Disconnect"</li>
              <li>Confirm the disconnection when prompted</li>
            </ol>

            <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">What Happens When You Disconnect</h3>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
              <li><strong>OAuth tokens are deleted</strong> - All Zoom authentication tokens are immediately removed from our database</li>
              <li><strong>Account association is removed</strong> - Your Zoom User ID is no longer linked to your Catch account</li>
              <li><strong>New meetings won't have Zoom</strong> - Catch will no longer be able to create Zoom meetings for you</li>
              <li><strong>Existing events unchanged</strong> - Calendar events that already have Zoom links remain as-is</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">Revoke Access from Zoom</h3>
            <p className="text-gray-700 mb-4">
              You can also revoke Catch's access directly from your Zoom account:
            </p>
            <ol className="list-decimal list-inside text-gray-700 mb-4 space-y-2">
              <li>Sign in to <a href="https://zoom.us" className="text-primary-600 hover:text-primary-700 underline" target="_blank" rel="noopener noreferrer">zoom.us</a></li>
              <li>Go to the Zoom App Marketplace</li>
              <li>Click "Manage" → "Installed Apps"</li>
              <li>Find "Catch" and click "Uninstall"</li>
            </ol>
          </section>

          {/* Troubleshooting Section */}
          <section id="troubleshooting" className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Troubleshooting</h2>

            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Connection Failed</h3>
                <p className="text-gray-700 mb-2">If the Zoom connection fails during authorization:</p>
                <ul className="list-disc list-inside text-gray-700 space-y-1">
                  <li>Make sure popup windows are not blocked in your browser</li>
                  <li>Try signing out of Zoom and signing back in before connecting</li>
                  <li>Clear your browser cache and try again</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Zoom Link Not Added to Meeting</h3>
                <p className="text-gray-700 mb-2">If Catch creates a calendar event but the Zoom link is missing:</p>
                <ul className="list-disc list-inside text-gray-700 space-y-1">
                  <li>Check that your Zoom account is still connected in Settings</li>
                  <li>Try disconnecting and reconnecting your Zoom account</li>
                  <li>Ensure your Zoom account has meeting creation privileges</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">"Unable to Create Zoom Meeting" Error</h3>
                <p className="text-gray-700 mb-2">This may occur if:</p>
                <ul className="list-disc list-inside text-gray-700 space-y-1">
                  <li>Your Zoom account has reached its meeting limit (free accounts)</li>
                  <li>Your Zoom subscription has expired</li>
                  <li>There's a temporary issue with Zoom's service</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Support Section */}
          <section className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Support</h2>
            <p className="text-gray-700 mb-4">
              Need help with the Zoom integration? We're here for you:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>
                <strong>Support Page</strong> -{' '}
                <Link to="/support" className="text-primary-600 hover:text-primary-700 underline">
                  Visit our support page
                </Link>
              </li>
              <li>
                <strong>Email</strong> -{' '}
                <a href="mailto:support@catchagent.ai" className="text-primary-600 hover:text-primary-700 underline">
                  support@catchagent.ai
                </a>
              </li>
            </ul>
          </section>

          {/* CTA Section */}
          <section className="bg-gradient-to-r from-primary-50 to-blue-50 rounded-xl p-6 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Ready to get started?</h2>
            <p className="text-gray-700 mb-4">
              Sign in to connect Catch with your Zoom account.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center px-6 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
            >
              Sign in to Get Started
            </Link>
          </section>
        </div>
      </div>
    </div>
  )
}
